import * as React from "react";
import { useImperativeHandle } from "react";

import { cn } from "@krea/shared/utils";

// biome-ignore lint/style/useNamingConvention: pre-existing name, renaming would break the public API
interface UseAutoSizeTextAreaProps {
  textAreaRef: React.MutableRefObject<HTMLTextAreaElement | null>;
  minHeight?: number;
  maxHeight?: number;
  triggerAutoSize: string;
}

const useAutoSizeTextArea = ({
  textAreaRef,
  triggerAutoSize,
  maxHeight = Number.MAX_SAFE_INTEGER,
  minHeight = 0,
}: UseAutoSizeTextAreaProps) => {
  const [init, setInit] = React.useState(true);
  // biome-ignore lint/correctness/useExhaustiveDependencies: triggerAutoSize is a string value used to trigger resize recalculation on content change
  React.useEffect(() => {
    // We need to reset the height momentarily to get the correct scrollHeight for the textarea
    const offsetBorder = 6;
    const textAreaElement = textAreaRef.current;

    if (textAreaElement) {
      if (init) {
        textAreaElement.style.minHeight = `${minHeight + offsetBorder}px`;

        if (maxHeight > minHeight) {
          textAreaElement.style.maxHeight = `${maxHeight}px`;
        }
        setInit(false);
      }

      // Save current scroll positions
      const currentScrollTop = textAreaElement.scrollTop;
      const currentScrollY = window.scrollY;

      // Temporarily hide overflow to prevent scroll jumps
      const originalOverflow = textAreaElement.style.overflow;
      textAreaElement.style.overflow = "hidden";

      textAreaElement.style.height = `${minHeight + offsetBorder}px`;
      const scrollHeight = textAreaElement.scrollHeight;

      // We then set the height directly, outside of the render loop
      // Trying to set this with state or a ref will product an incorrect value.
      if (scrollHeight > maxHeight) {
        textAreaElement.style.height = `${maxHeight}px`;
      } else {
        textAreaElement.style.height = `${scrollHeight + offsetBorder}px`;
      }

      // Restore overflow
      textAreaElement.style.overflow = originalOverflow;

      // Restore scroll positions
      textAreaElement.scrollTop = currentScrollTop;
      // Instant for the same reason as useBodyScrollLock: on the websites
      // `scroll-behavior: smooth` would turn this per-autosize restore into an
      // animated page scroll on every keystroke.
      window.scrollTo({
        top: currentScrollY,
        left: window.scrollX,
        behavior: "instant",
      });
    }
  }, [init, maxHeight, minHeight, textAreaRef, triggerAutoSize]);
};

// biome-ignore lint/style/useNamingConvention: pre-existing name, renaming would break the public API
export interface TextareaRef {
  textArea: HTMLTextAreaElement;
  maxHeight: number;
  minHeight: number;
}

type TextareaProps = {
  maxHeight?: number;
  minHeight?: number;
} & React.ComponentPropsWithoutRef<"textarea">;

export const Textarea = ({
  maxHeight = Number.MAX_SAFE_INTEGER,
  minHeight = 60,
  className,
  onChange,
  value,
  ref,
  ...props
}: TextareaProps & {
  ref?: React.Ref<TextareaRef>;
}) => {
  const textAreaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const [triggerAutoSize, setTriggerAutoSize] = React.useState("");

  useAutoSizeTextArea({
    textAreaRef,
    triggerAutoSize: triggerAutoSize,
    maxHeight,
    minHeight,
  });

  useImperativeHandle(ref, () => ({
    textArea: textAreaRef.current!,
    focus: () => textAreaRef.current?.focus(),
    maxHeight,
    minHeight,
  }));

  React.useEffect(() => {
    setTriggerAutoSize(value as string);
  }, [value]);

  return (
    <textarea
      {...props}
      value={value}
      ref={textAreaRef}
      className={cn(
        "flex w-full rounded-md border border-gray-200 bg-transparent px-3 py-2 text-base font-normal text-black shadow-md placeholder:text-gray-500 placeholder:font-normal focus-visible:outline-hidden focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      onChange={(e) => {
        setTriggerAutoSize(e.target.value);
        onChange?.(e);
      }}
    />
  );
};
Textarea.displayName = "Textarea";
